/** Old sessions without planningMode are driver-based. */
export function isFleetSession(assignmentData) {
  return assignmentData?.metadata?.planningMode === 'fleet';
}

export function isFleetDriver(driver) {
  return Array.isArray(driver?.fleet_windows) && driver.fleet_windows.length > 0;
}

export function formatFleetClock(iso, timezone = 'America/Los_Angeles') {
  if (iso == null || iso === '') return '';
  const raw = String(iso);
  if (/^\d{1,2}:\d{2}$/.test(raw)) {
    const [h, m] = raw.split(':');
    return `${h.padStart(2, '0')}:${m}`;
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: timezone,
  });
}

function instant(iso) {
  if (iso == null || iso === '') return NaN;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? NaN : t;
}

/** Inclusive start/end. Returns window_index or null. */
export function windowForTime(driver, iso) {
  if (!isFleetDriver(driver) || iso == null || iso === '') return null;
  const t = instant(iso);
  if (Number.isNaN(t)) return null;
  for (const window of driver.fleet_windows) {
    const start = instant(window.start);
    const end = instant(window.end);
    if (Number.isNaN(start) || Number.isNaN(end)) continue;
    if (start <= t && t <= end) return window.window_index;
  }
  return null;
}

export function coveringWindow(driver, isoOrTrip) {
  if (!isFleetDriver(driver)) return null;
  const iso = typeof isoOrTrip === 'string' || isoOrTrip == null
    ? isoOrTrip
    : (isoOrTrip.schedule_time || isoOrTrip.scheduleTime);
  const idx = windowForTime(driver, iso);
  if (idx == null) return null;
  return driver.fleet_windows.find((w) => w.window_index === idx) || null;
}

export function groupTripsByWindow(driver) {
  const windows = [...(driver?.fleet_windows || [])].sort(
    (a, b) => (a.window_index ?? 0) - (b.window_index ?? 0)
  );
  const byIdx = new Map(windows.map((w) => [w.window_index, { window: w, trips: [] }]));
  const none = { window: null, trips: [] };
  const sorted = [...(driver?.trips || [])].sort((a, b) => (a.sequence ?? 999) - (b.sequence ?? 999));
  for (const trip of sorted) {
    let idx = trip.fleet_window_index;
    if (idx === undefined || idx === '') {
      idx = windowForTime(driver, trip.schedule_time);
    }
    if (idx != null && byIdx.has(idx)) byIdx.get(idx).trips.push(trip);
    else none.trips.push(trip);
  }
  const groups = windows.map((w) => byIdx.get(w.window_index));
  if (none.trips.length) groups.push(none);
  return groups;
}

function windowIndexFromPseudo(bucket) {
  if (bucket?.window_index != null && bucket.window_index !== '') return bucket.window_index;
  const id = String(bucket?.driver_id || '');
  const hash = id.lastIndexOf('#');
  if (hash < 0) return null;
  const n = Number(id.slice(hash + 1));
  return Number.isFinite(n) ? n : null;
}

export function splitDriverForEstimate(driver) {
  if (!isFleetDriver(driver)) return [driver];
  return groupTripsByWindow(driver)
    .filter((g) => g.window)
    .map((g) => {
      const { fleet_windows, ...rest } = driver;
      return {
        ...rest,
        driver_id: `${driver.driver_id}#${g.window.window_index}`,
        window_index: g.window.window_index,
        window_end: g.window.end,
        ref_latitude: g.window.start_latitude ?? driver.ref_latitude,
        ref_longitude: g.window.start_longitude ?? driver.ref_longitude,
        return_latitude: g.window.return_latitude,
        return_longitude: g.window.return_longitude,
        shift_window: { shift_start: g.window.start, shift_end: g.window.end, has_shift: true },
        trips: g.trips,
        fleet_los_title: g.window.los_title,
        fleet_space_type_title: g.window.space_type_title,
        fleet_los_image_url: g.window.los_image_url,
        fleet_capacity: null,
      };
    });
}

export function stitchEstimates(driver, estimatedPseudoBuckets) {
  const byTrip = new Map();
  const windows = (driver.fleet_windows || []).map((window) => ({ ...window }));
  for (const bucket of estimatedPseudoBuckets || []) {
    for (const trip of bucket.trips || []) {
      byTrip.set(String(trip.trip_id || trip.id), trip);
    }
    const idx = windowIndexFromPseudo(bucket);
    const window = windows.find((item) => item.window_index === idx);
    if (!window) continue;
    if (bucket.garage_return_miles != null) window.garage_return_miles = bucket.garage_return_miles;
    if (bucket.garage_return_minutes != null) window.garage_return_minutes = bucket.garage_return_minutes;
    if (bucket.garage_leave_eta != null) window.garage_leave_eta = bucket.garage_leave_eta;
    if (bucket.garage_return_eta != null) window.garage_return_eta = bucket.garage_return_eta;
    if (bucket.garage_return_late != null) window.garage_return_late = bucket.garage_return_late;
  }
  return {
    ...driver,
    fleet_windows: windows,
    trips: (driver.trips || []).map((trip) => {
      const est = byTrip.get(String(trip.trip_id || trip.id));
      if (!est) return trip;
      return {
        ...trip,
        trip_details: est.trip_details ?? trip.trip_details,
        deadhead_miles: est.deadhead_miles ?? trip.deadhead_miles,
        eta_to_pickup_minutes: est.eta_to_pickup_minutes ?? trip.eta_to_pickup_minutes,
        loadTime: est.loadTime ?? trip.loadTime,
        unloadTime: est.unloadTime ?? trip.unloadTime,
      };
    }),
  };
}

export function applyWindowIndex(driver, trip) {
  if (!isFleetDriver(driver) || !trip) return trip;
  return { ...trip, fleet_window_index: windowForTime(driver, trip.schedule_time) };
}

export function fleetChipLabel(driver) {
  const windows = driver?.fleet_windows || [];
  if (!windows.length) return '';
  const n = windows.length;
  return `${n} fleet${n === 1 ? '' : 's'}`;
}

export function formatDriveMinutes(minutes) {
  if (!Number.isFinite(minutes) || minutes <= 0) return '0 min';
  const rounded = Math.max(1, Math.round(minutes));
  if (rounded < 60) return `${rounded} min`;
  const hours = Math.floor(rounded / 60);
  const rest = rounded % 60;
  return rest ? `${hours}h ${rest}m` : `${hours}h`;
}

function finiteOrNull(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** Backend garage stamp only (GIS when USE_GIS_API, else haversine). No frontend distance math. */
export function windowGarageReturn(driver, window) {
  const miles = finiteOrNull(window?.garage_return_miles);
  const driveMinutes = finiteOrNull(window?.garage_return_minutes);
  const leaveMs = instant(window?.garage_leave_eta);
  const arriveMs = instant(window?.garage_return_eta);
  const windowEnd = instant(window?.end);
  const late = Boolean(window?.garage_return_late)
    || (Number.isFinite(arriveMs) && Number.isFinite(windowEnd) && arriveMs > windowEnd);
  return {
    miles,
    driveMinutes,
    leaveMs: Number.isFinite(leaveMs) ? leaveMs : null,
    arriveMs: Number.isFinite(arriveMs) ? arriveMs : null,
    late,
  };
}

export function windowAwayMiles(driver, window) {
  return windowGarageReturn(driver, window).miles || 0;
}

export function lastWindowAwayMiles(driver) {
  if (!isFleetDriver(driver)) return 0;
  const windows = [...(driver.fleet_windows || [])].sort(
    (a, b) => (a.window_index ?? 0) - (b.window_index ?? 0)
  );
  const last = windows[windows.length - 1];
  return windowAwayMiles(driver, last);
}

/** View of one covering window so existing shift/LOS checks stay driver-shaped. */
export function driverViewForTrip(driver, trip) {
  if (!isFleetDriver(driver)) return driver;
  const window = coveringWindow(driver, trip);
  if (!window) return null;
  const group = groupTripsByWindow(driver).find((g) => g.window && g.window.window_index === window.window_index);
  return {
    ...driver,
    fleet_los_title: window.los_title,
    fleet_space_type_title: window.space_type_title,
    fleet_los_image_url: window.los_image_url,
    shift_window: { shift_start: window.start, shift_end: window.end, has_shift: true },
    ref_latitude: window.start_latitude ?? driver.ref_latitude,
    ref_longitude: window.start_longitude ?? driver.ref_longitude,
    trips: group?.trips || [],
  };
}
